import { useEffect, useMemo } from "react";
import { useProgressBar } from "context/ProgressBarContext";
import { computeProgressPercent, ProgressInputs } from "utils/contactProgress";

/**
 * Computes the contact page's completion percentage from the watched form
 * slices and pushes it into the progress bar (step 0) whenever it changes.
 *
 * @param inputs - Watched card slices plus the product flags that decide which
 *                 sections count toward completion.
 * @returns The current 0-100 progress percentage.
 */
export const useContactProgress = (inputs: ProgressInputs): number => {
    const { updateStepCompletion } = useProgressBar();
    const {
        contactInfo,
        statementOfHealthData,
        coverageReplacementData,
        policyBeneficiaryData,
        secondaryAddresseeData,
        isDTO,
        isGIWL,
    } = inputs;

    const progressPercent = useMemo(
        () =>
            computeProgressPercent({
                contactInfo,
                statementOfHealthData,
                coverageReplacementData,
                policyBeneficiaryData,
                secondaryAddresseeData,
                isDTO,
                isGIWL,
            }),
        [
            contactInfo,
            statementOfHealthData,
            coverageReplacementData,
            policyBeneficiaryData,
            secondaryAddresseeData,
            isDTO,
            isGIWL,
        ]
    );

    useEffect(() => {
        updateStepCompletion(0, progressPercent);
    }, [progressPercent, updateStepCompletion]);

    return progressPercent;
};
